import { useEffect, useMemo, useState } from 'react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { cn } from '@/lib/utils';
import { AlertCircle } from 'lucide-react';
import {
  DAY_START_HOUR,
  DAY_END_HOUR,
  minutesFromDayStart,
  detectOverlaps,
  isSameLocalDay,
} from '@/lib/agenda-timeline';
import { resolveOSDuration } from '@/services/ordens-servico.service';
import type { AgendaOS } from '@/services/agenda.service';
import type { OSStatus } from './OSBlock';

interface Prestador {
  id: string;
  nome: string;
}

interface WeekCalendarViewProps {
  view: 'week' | 'day';
  viewDate: Date;
  prestadores: Prestador[]; // for displaying prestador name inside blocks
  ordens: AgendaOS[];
  onOSClick: (osId: string) => void;
  /** Quando true, os blocos não são arrastáveis (vista do prestador). */
  readOnly?: boolean;
}

const HOUR_HEIGHT_PX = 48;

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const HOUR_GRIDLINE_STYLE: React.CSSProperties = {
  backgroundImage:
    'repeating-linear-gradient(to bottom, transparent 0, transparent calc(100%/12 - 1px), hsl(var(--border-soft)) calc(100%/12 - 1px), hsl(var(--border-soft)) calc(100%/12))',
};

export function WeekCalendarView(props: WeekCalendarViewProps) {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const days = useMemo(() => {
    if (props.view === 'day') return [props.viewDate];
    const dow = props.viewDate.getDay();
    const offsetFromMonday = dow === 0 ? 6 : dow - 1;
    const monday = addDays(props.viewDate, -offsetFromMonday);
    return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  }, [props.view, props.viewDate]);

  const hours = Array.from({ length: DAY_END_HOUR - DAY_START_HOUR }, (_, i) => DAY_START_HOUR + i);

  const overlapping = useMemo(
    () =>
      detectOverlaps(
        props.ordens
          .filter((os) => os.data_agendada)
          .map((os) => ({
            id: os.id,
            prestadorId: os.prestador_id,
            start: new Date(os.data_agendada as string),
            durationMinutes: resolveOSDuration({
              duracao_estimada_minutos: os.duracao_estimada_minutos,
              itens: os.itens,
            }),
          })),
      ),
    [props.ordens],
  );

  const prestadorNomeMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of props.prestadores) map.set(p.id, p.nome);
    return map;
  }, [props.prestadores]);

  const bodyHeight = hours.length * HOUR_HEIGHT_PX;

  return (
    <div className="flex flex-1 flex-col overflow-auto">
      {/* Day headers (sticky) */}
      <div className="sticky top-0 z-10 grid grid-cols-[60px_1fr] border-b border-border-soft bg-muted/30">
        <div />
        <div
          className="grid"
          style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)` }}
        >
          {days.map((d, i) => (
            <div
              key={i}
              className="border-l border-border-soft p-2 text-center text-[11px] uppercase tracking-wide font-semibold text-muted-foreground"
            >
              {props.view === 'day'
                ? d.toLocaleDateString('pt-PT', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  })
                : d.toLocaleDateString('pt-PT', { weekday: 'short', day: 'numeric' })}
            </div>
          ))}
        </div>
      </div>

      {/* Body: hour gutter + day columns */}
      <div className="grid grid-cols-[60px_1fr]" style={{ height: bodyHeight }}>
        {/* Hour labels (left gutter) */}
        <div className="flex flex-col">
          {hours.map((h) => (
            <div
              key={h}
              style={{ height: HOUR_HEIGHT_PX }}
              className="flex items-start justify-end pr-2 pt-0.5 text-[11px] font-mono text-muted-foreground"
            >
              {String(h).padStart(2, '0')}:00
            </div>
          ))}
        </div>

        {/* Day columns */}
        <div
          className="grid"
          style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)` }}
        >
          {days.map((day) => (
            <DayColumn
              key={day.toISOString()}
              day={day}
              ordens={props.ordens.filter(
                (os) =>
                  os.data_agendada && dayKey(new Date(os.data_agendada)) === dayKey(day),
              )}
              overlapping={overlapping}
              prestadorNomeMap={prestadorNomeMap}
              now={now}
              onOSClick={props.onOSClick}
              readOnly={props.readOnly}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface DayColumnProps {
  day: Date;
  ordens: AgendaOS[];
  overlapping: Set<string>;
  prestadorNomeMap: Map<string, string>;
  now: Date;
  onOSClick: (id: string) => void;
  readOnly?: boolean;
}

function DayColumn(props: DayColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `slot-week-${props.day.toISOString()}`,
    data: { type: 'slot-week', day: props.day.toISOString() },
    disabled: props.readOnly ?? false,
  });

  // Now-indicator: horizontal red line at current time, only on today's column
  const isToday = isSameLocalDay(props.day, props.now);
  const nowMinutes = isToday ? minutesFromDayStart(props.now) : null;
  const totalMinutes = (DAY_END_HOUR - DAY_START_HOUR) * 60;
  const nowTopPct =
    nowMinutes !== null && nowMinutes >= 0 && nowMinutes <= totalMinutes
      ? (nowMinutes / totalMinutes) * 100
      : null;

  return (
    <div
      ref={setNodeRef}
      style={HOUR_GRIDLINE_STYLE}
      className={cn(
        'relative border-l border-border-soft transition-colors',
        isOver && !props.readOnly && 'bg-muted/40',
      )}
    >
      {nowTopPct !== null && (
        <div
          aria-hidden
          className="pointer-events-none absolute left-0 right-0 h-px bg-foreground z-10"
          style={{ top: `${nowTopPct}%` }}
        />
      )}
      {props.ordens.map((os) => {
        const start = new Date(os.data_agendada as string);
        const duration = resolveOSDuration({
          duracao_estimada_minutos: os.duracao_estimada_minutos,
          itens: os.itens,
        });
        const topMin = minutesFromDayStart(start);
        const topPct = (topMin / totalMinutes) * 100;
        const heightPct = (duration / totalMinutes) * 100;
        // Serviços: junta nomes únicos dos itens. Em geral é só 1, mas se
        // houver mais de um o nome do primeiro com sufixo "+N" cabe melhor
        // que listar tudo dentro de um bloco estreito.
        const nomesServico = Array.from(
          new Set(os.itens.map((i) => i.servico_nome).filter((n): n is string => !!n)),
        );
        const servicoLabel =
          nomesServico.length === 0
            ? null
            : nomesServico.length === 1
              ? nomesServico[0]
              : `${nomesServico[0]} +${nomesServico.length - 1}`;
        return (
          <WeekOSBlock
            key={os.id}
            id={os.id}
            clienteNome={os.cliente?.nome || 'Sem cliente'}
            servicoNome={servicoLabel}
            prestadorNome={
              os.prestador_id ? props.prestadorNomeMap.get(os.prestador_id) ?? null : null
            }
            start={start}
            durationMinutes={duration}
            status={os.status as OSStatus}
            topPct={topPct}
            heightPct={heightPct}
            overlapping={props.overlapping.has(os.id)}
            onClick={props.onOSClick}
            readOnly={props.readOnly}
          />
        );
      })}
    </div>
  );
}

interface WeekOSBlockProps {
  id: string;
  clienteNome: string;
  servicoNome: string | null;
  prestadorNome: string | null;
  start: Date;
  durationMinutes: number;
  status: OSStatus;
  topPct: number;
  heightPct: number;
  overlapping: boolean;
  onClick: (id: string) => void;
  readOnly?: boolean;
}

const STATUS_CLASSES: Record<OSStatus, string> = {
  pendente: 'bg-muted text-foreground border border-dashed border-foreground/60',
  aceita: 'bg-[hsl(0_0%_88%)] text-foreground border border-foreground/40',
  em_execucao: 'bg-foreground text-background border border-foreground animate-pulse',
  concluida: 'bg-muted text-muted-foreground border border-border-soft',
  cancelada: 'bg-muted/60 text-muted-foreground border border-border-soft line-through',
  recusada: 'bg-muted/60 text-muted-foreground border border-border-soft line-through',
  excluida: 'hidden',
};

const NON_DRAGGABLE: OSStatus[] = ['em_execucao', 'concluida', 'cancelada', 'recusada', 'excluida'];

function formatHHmm(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function WeekOSBlock(props: WeekOSBlockProps) {
  const draggable = !props.readOnly && !NON_DRAGGABLE.includes(props.status);
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: props.id,
    data: { type: 'os-block', osId: props.id, status: props.status },
    disabled: !draggable,
  });

  const style: React.CSSProperties = {
    top: `${props.topPct}%`,
    height: `${props.heightPct}%`,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
  };

  const end = new Date(props.start.getTime() + props.durationMinutes * 60000);
  const compact = props.heightPct < 7; // <50min → tight rendering
  const showServico = !compact && !!props.servicoNome;
  const showPrestador = props.heightPct >= 14 && !!props.prestadorNome;
  const titleParts = [
    props.clienteNome,
    props.servicoNome,
    `${formatHHmm(props.start)}–${formatHHmm(end)}`,
    props.prestadorNome,
  ].filter(Boolean);

  return (
    <div
      ref={setNodeRef}
      data-status={props.status}
      style={style}
      onClick={() => props.onClick(props.id)}
      {...(draggable ? { ...listeners, ...attributes, role: 'button' as const } : {})}
      className={cn(
        'absolute left-1 right-1 overflow-hidden rounded-sm px-2 py-1 text-xs shadow-xs',
        STATUS_CLASSES[props.status],
        draggable && 'cursor-grab active:cursor-grabbing',
        props.overlapping && 'ring-2 ring-foreground',
      )}
      title={titleParts.join(' — ')}
    >
      <div className="truncate font-medium leading-tight">{props.clienteNome}</div>
      {showServico && (
        <div className="truncate text-[0.65rem] opacity-80 leading-tight">
          {props.servicoNome}
        </div>
      )}
      {!compact && (
        <div className="truncate text-[0.65rem] opacity-80 leading-tight">
          {formatHHmm(props.start)}–{formatHHmm(end)}
        </div>
      )}
      {showPrestador && (
        <div className="truncate text-[0.65rem] opacity-70 leading-tight">
          {props.prestadorNome}
        </div>
      )}
      {props.overlapping && (
        <AlertCircle className="absolute right-1 top-1 h-3 w-3 text-foreground" />
      )}
    </div>
  );
}
